/* eslint-disable @next/next/no-img-element */
import React from "react";

/**
 * Minimal Markdown renderer. No external dependencies. Supports the
 * subset of Markdown a blog actually uses:
 *
 *   # H1, ## H2, ### H3       — ATX headings
 *   **bold**                  — strong
 *   *italic* or _italic_      — em
 *   `inline code`             — code
 *   [text](url)               — links
 *   ![alt](url)               — images
 *   - / *  list items         — unordered lists
 *   1. 2. 3. list items       — ordered lists
 *   > quote                   — blockquote
 *   ```lang … ```             — fenced code block
 *   ---                       — horizontal rule
 *
 * What it intentionally does NOT do:
 *   - HTML pass-through (everything is text, no XSS risk)
 *   - Tables, footnotes, task lists, autolinks
 *   - Deeply nested emphasis with overlapping markers
 *
 * If you ever need those, swap this for `react-markdown` + `remark-gfm`.
 */

type ListKind = "bullet" | "ordered" | "alpha-lower" | "alpha-upper";

type Block =
  | { type: "heading"; level: 1 | 2 | 3 | 4 | 5 | 6; content: string }
  | { type: "paragraph"; content: string }
  | { type: "code"; lang: string; content: string }
  | { type: "list"; kind: ListKind; items: string[] }
  | { type: "blockquote"; content: string }
  | { type: "hr" };

const isHeading = (l: string) => /^#{1,6}\s+/.test(l);
const isFence = (l: string) => /^```/.test(l);
const isHr = (l: string) => /^\s*-{3,}\s*$/.test(l);
const isUlItem = (l: string) => /^\s*[-*]\s+/.test(l);
const isOlItem = (l: string) => /^\s*\d+\.\s+/.test(l);
const isAlphaLowerItem = (l: string) => /^\s*[a-z]\.\s+/.test(l);
const isAlphaUpperItem = (l: string) => /^\s*[A-Z]\.\s+/.test(l);
const isQuote = (l: string) => /^>\s?/.test(l);

const parseBlocks = (md: string): Block[] => {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (isFence(line)) {
      const lang = line.replace(/^```/, "").trim();
      const start = i + 1;
      let j = start;
      while (j < lines.length && !isFence(lines[j])) j++;
      out.push({ type: "code", lang, content: lines.slice(start, j).join("\n") });
      i = j + 1;
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      out.push({
        type: "heading",
        level: h[1].length as 1 | 2 | 3 | 4 | 5 | 6,
        content: h[2],
      });
      i++;
      continue;
    }

    // Horizontal rule
    if (isHr(line)) {
      out.push({ type: "hr" });
      i++;
      continue;
    }

    // Unordered list
    if (isUlItem(line)) {
      const items: string[] = [];
      while (i < lines.length && isUlItem(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      out.push({ type: "list", kind: "bullet", items });
      continue;
    }

    // Ordered list (numbered)
    if (isOlItem(line)) {
      const items: string[] = [];
      while (i < lines.length && isOlItem(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      out.push({ type: "list", kind: "ordered", items });
      continue;
    }

    // Alphabetic list — lowercase: `a. item`, `b. item`, …
    if (isAlphaLowerItem(line)) {
      const items: string[] = [];
      while (i < lines.length && isAlphaLowerItem(lines[i])) {
        items.push(lines[i].replace(/^\s*[a-z]\.\s+/, ""));
        i++;
      }
      out.push({ type: "list", kind: "alpha-lower", items });
      continue;
    }

    // Alphabetic list — uppercase: `A. item`, `B. item`, …
    if (isAlphaUpperItem(line)) {
      const items: string[] = [];
      while (i < lines.length && isAlphaUpperItem(lines[i])) {
        items.push(lines[i].replace(/^\s*[A-Z]\.\s+/, ""));
        i++;
      }
      out.push({ type: "list", kind: "alpha-upper", items });
      continue;
    }

    // Blockquote
    if (isQuote(line)) {
      const buf: string[] = [];
      while (i < lines.length && isQuote(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      out.push({ type: "blockquote", content: buf.join("\n") });
      continue;
    }

    // Blank line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph — consume until blank or a new block starts
    const buf: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !isHeading(lines[i]) &&
      !isFence(lines[i]) &&
      !isHr(lines[i]) &&
      !isUlItem(lines[i]) &&
      !isOlItem(lines[i]) &&
      !isAlphaLowerItem(lines[i]) &&
      !isAlphaUpperItem(lines[i]) &&
      !isQuote(lines[i])
    ) {
      buf.push(lines[i]);
      i++;
    }
    out.push({ type: "paragraph", content: buf.join("\n") });
  }

  return out;
};

/**
 * Render the inline span (text inside paragraphs / headings / list items)
 * into React nodes. Recurses for nested emphasis (e.g. **bold *and italic***).
 *
 * Inline patterns, in order of precedence:
 *   1. images       ![alt](url)
 *   2. links        [text](url)
 *   3. inline code  `code`
 *   4. bold         **text** or __text__
 *   5. italic       *text* or _text_
 *   6. line break   trailing two-space + newline
 */
const renderInline = (text: string, keyPrefix = ""): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let rest = text;
  let k = 0;

  const next = (n: React.ReactNode) => nodes.push(n);

  while (rest.length > 0) {
    // Image
    let m = rest.match(/^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/);
    if (m) {
      next(
        <img
          key={`${keyPrefix}i${k++}`}
          src={m[2]}
          alt={m[1]}
          title={m[3]}
          className="rounded-md inline-block max-w-full h-auto"
        />
      );
      rest = rest.slice(m[0].length);
      continue;
    }
    // Link
    m = rest.match(/^\[([^\]]+)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/);
    if (m) {
      next(
        <a
          key={`${keyPrefix}a${k++}`}
          href={m[2]}
          title={m[3]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {renderInline(m[1], `${keyPrefix}a${k}_`)}
        </a>
      );
      rest = rest.slice(m[0].length);
      continue;
    }
    // Inline code — no inline parsing inside
    m = rest.match(/^`([^`\n]+)`/);
    if (m) {
      next(
        <code
          key={`${keyPrefix}c${k++}`}
          className="px-1 py-0.5 rounded bg-dark-gray text-text-primary-ligt text-[0.9em] font-mono"
        >
          {m[1]}
        </code>
      );
      rest = rest.slice(m[0].length);
      continue;
    }
    // Bold (** or __)
    m = rest.match(/^\*\*([\s\S]+?)\*\*/) || rest.match(/^__([\s\S]+?)__/);
    if (m) {
      next(
        <strong key={`${keyPrefix}b${k++}`}>
          {renderInline(m[1], `${keyPrefix}b${k}_`)}
        </strong>
      );
      rest = rest.slice(m[0].length);
      continue;
    }
    // Italic (* or _) — avoid matching ** by requiring a non-* after
    m =
      rest.match(/^\*(?!\*)([\s\S]+?)\*(?!\*)/) ||
      rest.match(/^_(?!_)([\s\S]+?)_(?!_)/);
    if (m) {
      next(
        <em key={`${keyPrefix}e${k++}`}>
          {renderInline(m[1], `${keyPrefix}e${k}_`)}
        </em>
      );
      rest = rest.slice(m[0].length);
      continue;
    }
    // Hard line break — "  \n"
    if (rest.startsWith("  \n")) {
      next(<br key={`${keyPrefix}br${k++}`} />);
      rest = rest.slice(3);
      continue;
    }

    // No match — emit chars up to the next potentially-special char as plain text.
    const nextSpecial = rest.search(/[!\[`*_]|  \n/);
    if (nextSpecial === -1) {
      next(rest);
      break;
    }
    if (nextSpecial > 0) {
      next(rest.slice(0, nextSpecial));
      rest = rest.slice(nextSpecial);
    } else {
      // The special char doesn't form a valid pattern; emit it literally and move on.
      next(rest[0]);
      rest = rest.slice(1);
    }
  }

  return nodes;
};

const renderBlock = (block: Block, idx: number): React.ReactNode => {
  switch (block.type) {
    case "heading": {
      const H = (`h${block.level}` as unknown) as
        | "h1"
        | "h2"
        | "h3"
        | "h4"
        | "h5"
        | "h6";
      const sizeClass =
        block.level === 1
          ? "text-3xl font-semibold"
          : block.level === 2
          ? "text-2xl font-semibold"
          : block.level === 3
          ? "text-xl font-semibold"
          : "text-lg font-semibold";
      return (
        <H key={idx} className={`${sizeClass} mt-6 mb-2 leading-tight`}>
          {renderInline(block.content, `h${idx}_`)}
        </H>
      );
    }
    case "paragraph":
      return (
        <p key={idx} className="md:text-justify leading-relaxed">
          {renderInline(block.content, `p${idx}_`)}
        </p>
      );
    case "code":
      return (
        <pre
          key={idx}
          className="bg-dark-gray rounded-md text-zinc-100 text-sm p-4 overflow-x-auto leading-relaxed my-4"
          aria-label={block.lang ? `${block.lang} code` : "code"}
        >
          <code>{block.content}</code>
        </pre>
      );
    case "list": {
      const items = block.items.map((it, i) => (
        <li key={i}>{renderInline(it, `l${idx}_${i}_`)}</li>
      ));
      if (block.kind === "bullet") {
        return (
          <ul key={idx} className="space-y-2 list-disc ml-6">
            {items}
          </ul>
        );
      }
      // ordered / alphabetic — all <ol>, just different list-style.
      const olClass =
        block.kind === "alpha-lower"
          ? "space-y-2 list-[lower-alpha] ml-6"
          : block.kind === "alpha-upper"
          ? "space-y-2 list-[upper-alpha] ml-6"
          : "space-y-2 list-decimal ml-6";
      const typeAttr =
        block.kind === "alpha-lower"
          ? "a"
          : block.kind === "alpha-upper"
          ? "A"
          : undefined;
      return (
        <ol key={idx} type={typeAttr} className={olClass}>
          {items}
        </ol>
      );
    }
    case "blockquote":
      return (
        <blockquote
          key={idx}
          className="border-l-4 border-primary/40 pl-4 italic text-light-text/80 my-4"
        >
          {block.content.split("\n\n").map((para, i) => (
            <p key={i} className="leading-relaxed">
              {renderInline(para, `q${idx}_${i}_`)}
            </p>
          ))}
        </blockquote>
      );
    case "hr":
      return <hr key={idx} className="my-8 border-t border-text/10" />;
  }
};

export default function MarkdownRenderer({ source }: { source: string }) {
  const blocks = parseBlocks(source);
  return <>{blocks.map(renderBlock)}</>;
}
